/** @file Draws placed labels: leader lines, then translucent boxes, then glyphs (three batched passes). */
import type { Sink } from '../draw/createSink';
import { emitLine } from '../draw/emitLine';
import { emitRect } from '../draw/emitRect';
import { emitText } from '../draw/emitText';
import { OVERLAY_STYLE } from '../overlayStyle';
import { layoutLabels, type LabelLayout } from './layoutLabels';
import type { LabelRequest } from './placeLabels';

const seg = new Float64Array(4);

/** Leader lines from the anchor to the nearest point of the box, for labels pushed out to an outer ring. */
function leaders(sink: Sink, labels: readonly LabelRequest[], { count, index, box, leader }: LabelLayout): void {
  for (let k = 0; k < count; k++) {
    const label = labels[index[k] ?? -1], o = 4 * k;
    if (leader[k] !== 1 || label === undefined) continue;
    const x = box[o] ?? 0, y = box[o + 1] ?? 0;
    seg[0] = label.x;
    seg[1] = label.y;
    seg[2] = Math.max(x, Math.min(label.x, x + (box[o + 2] ?? 0)));
    seg[3] = Math.max(y, Math.min(label.y, y + (box[o + 3] ?? 0)));
    emitLine(sink, seg, 1, OVERLAY_STYLE.leader);
  }
}

/** The translucent box of every placed label. */
function boxes(sink: Sink, { count, box }: LabelLayout): void {
  for (let o = 0; o < 4 * count; o += 4) emitRect(sink, box[o] ?? 0, box[o + 1] ?? 0, box[o + 2] ?? 0, box[o + 3] ?? 0, OVERLAY_STYLE.labelBox);
}

/** The texts, `OVERLAY_STYLE.labelPad` inside their boxes. */
function texts(sink: Sink, labels: readonly LabelRequest[], { count, index, box }: LabelLayout): void {
  const pad = OVERLAY_STYLE.labelPad;
  for (let k = 0; k < count; k++) {
    const label = labels[index[k] ?? -1];
    if (label !== undefined) emitText(sink, label.text, (box[4 * k] ?? 0) + pad, (box[4 * k + 1] ?? 0) + pad, label.color);
  }
}

/**
 * Places `labels` (layoutLabels; `ends` = exclusive end index of each layer's requests) and emits them on top of
 * everything drawn before. Returns how many visible labels the per-layer cap dropped.
 */
export function drawLabels(sink: Sink, labels: readonly LabelRequest[], ends?: readonly number[]): number {
  const layout = layoutLabels(labels, sink.width, sink.height, sink.atlas.glyphW, sink.atlas.glyphH, ends);
  leaders(sink, labels, layout);
  boxes(sink, layout);
  texts(sink, labels, layout);
  return layout.dropped;
}
